import { useState, useEffect, useCallback } from "react";
import { CheckCircle, AlertCircle, LogOut, Plus, Users, Pencil,
  X, Dumbbell, ShoppingCart, BarChart2, BookOpen, Building2,
  Tag, PlusCircle, Euro, Shield, UtensilsCrossed, CalendarDays, CreditCard, MessageCircle } from "lucide-react";

import AdminLogin        from "./AdminLogin";
import SchemaBuilder     from "./SchemaBuilder";

// Admin sub-tabs
import { useAdminApi }      from "./admin/useAdminApi";
import CreateUserModal      from "./admin/CreateUserModal";
import ConversatieModal     from "./admin/ConversatieModal";
import CursistenTab         from "./admin/CursistenTab";
import OefenbibliotheekTab  from "./admin/OefenbibliotheekTab";
import VoortgangTab         from "./admin/VoortgangTab";
import BestellingenTab      from "./admin/BestellingenTab";
import SchemasView          from "./admin/SchemasView";
import SchemaEditorView     from "./admin/SchemaEditorView";
import AnalyticsTab         from "./admin/AnalyticsTab";
import BedrijvenTab         from "./admin/BedrijvenTab";
import FacturenTab          from "./admin/FacturenTab";
import AbonnementenTab      from "./admin/AbonnementenTab";
import SchemaTemplatesTab   from "./admin/SchemaTemplatesTab";
import KortingcodesTab      from "./admin/KortingcodesTab";
import TrainModusTab        from "./admin/TrainModusTab";
import VoedingTab           from "./admin/VoedingTab";
import AdminRoosterTab      from "./admin/RoosterTab";
import AdminCreditsTab      from "./admin/CreditsTab";
import AdminCommunityTab    from "./bedrijf/CommunityTab";
import CursistSchemaPanel   from "./admin/CursistSchemaPanel";
import PTVerkoopTab         from "./admin/PTVerkoopTab";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl animate-fadeIn
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

const TABS = [
  { key: "cursisten",    label: "Cursisten",       icon: <Users size={14} /> },
  { key: "schemas",      label: "Schema's",         icon: <Dumbbell size={14} /> },
  { key: "bewerken",     label: "Bewerken",         icon: <Pencil size={14} /> },
  { key: "maker",        label: "Schema Maker",     icon: <PlusCircle size={14} /> },
  { key: "bestellingen", label: "Bestellingen",     icon: <ShoppingCart size={14} /> },
  { key: "ptverkoop",    label: "PT Schema's",      icon: <ShoppingCart size={14} /> },
  { key: "facturen",     label: "Facturen",         icon: <Euro size={14} /> },
  { key: "abonnementen", label: "Abonnementen",     icon: <Shield size={14} /> },
  { key: "templates",    label: "Schema Templates", icon: <BookOpen size={14} /> },
  { key: "voortgang",    label: "Voortgang",        icon: <BarChart2 size={14} /> },
  { key: "voeding",      label: "Voeding",          icon: <UtensilsCrossed size={14} /> },
  { key: "rooster",      label: "Rooster",          icon: <CalendarDays size={14} /> },
  { key: "credits",      label: "Credits",          icon: <CreditCard size={14} /> },
  { key: "community",    label: "Community",        icon: <MessageCircle size={14} /> },
  { key: "oefeningen",   label: "Oefeningen",       icon: <BookOpen size={14} /> },
  { key: "analyse",      label: "Analytics",        icon: <BarChart2 size={14} /> },
  { key: "bedrijven",    label: "Bedrijven",        icon: <Building2 size={14} /> },
  { key: "kortingscodes",label: "Kortingscodes",    icon: <Tag size={14} /> },
  { key: "trainmodus",   label: "Train Modus",      icon: <Dumbbell size={14} /> },
];

export default function AdminPanel() {
  const [adminUser,          setAdminUser]         = useState(() => {
    const token = localStorage.getItem("fitsmith_admin_token");
    return token ? { token } : null;
  });
  const [users,              setUsers]             = useState([]);
  const [stats,              setStats]             = useState({});
  const [loading,            setLoading]           = useState(true);
  const [showCreate,         setShowCreate]        = useState(false);
  const [toast,              setToast]             = useState(null);
  const [activeTab,          setActiveTab]         = useState("cursisten");
  const [trainerBerichtModal,setTrainerBerichtModal] = useState(null);
  const [cursistSchemaPanel, setCursistSchemaPanel] = useState(null);
  const [unreadCounts,       setUnreadCounts]      = useState({});

  const api = useAdminApi(adminUser?.token || "");
  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    if (!adminUser) return;
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.getUsers(), api.getStats()]);
      setUsers(Array.isArray(u) ? u : []);
      setStats(s || {});
    } catch (err) {
      if (err?.status === 401) {
        localStorage.removeItem("fitsmith_admin_token");
        setAdminUser(null);
        return;
      }
      showToast("Laden mislukt", "error");
    }
    finally { setLoading(false); }

    fetch(`${API}/api/admin/berichten/overzicht`, {
      headers: { Authorization: `Bearer ${adminUser.token}` },
    }).then(r => r.ok ? r.json() : {}).then(d => setUnreadCounts(d)).catch(() => {});
  }, [adminUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (adminUser) load(); }, [adminUser, load]);

  const handleUserDeleted = (id) => {
    setUsers(u => u.filter(x => x.id !== id));
    setStats(s => ({ ...s, total: Math.max(0, (s.total || 1) - 1), active: Math.max(0, (s.active || 1) - 1) }));
  };

  const handleUserUpdated = (patch) => {
    setUsers(u => u.map(x => x.id === patch.id ? { ...x, ...patch } : x));
  };

  const handleUnreadCleared = (userId) => {
    setUnreadCounts(prev => ({ ...prev, [userId]: 0 }));
  };

  const handleLogout = () => {
    localStorage.removeItem("fitsmith_admin_token");
    setAdminUser(null);
  };

  if (!adminUser) return <AdminLogin onLogin={setAdminUser} />;

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Navbar */}
      <nav className="bg-[#0a0a0a] border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="FitSmith" className="h-8 w-8 object-contain" />
          <div>
            <span className="text-white font-bold tracking-wide">FitSmith</span>
            <span className="text-[#C9A84C] font-bold"> Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-zinc-500 hover:text-white text-sm transition-colors">← Website</a>
          <button onClick={handleLogout} className="btn-course-outline text-xs px-3 py-1.5 flex items-center gap-1.5">
            <LogOut size={12} /> Uitloggen
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Beheer</p>
            <h1 className="text-2xl font-bold text-white">Admin Paneel</h1>
          </div>
          {activeTab === "cursisten" && (
            <button onClick={() => setShowCreate(true)} data-testid="open-create-modal"
              className="btn-gold-course px-5 py-2.5 text-sm font-bold uppercase tracking-wide flex items-center gap-2">
              <Plus size={15} /> Nieuw account
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#111] border border-white/5 rounded-xl p-1 mb-8 w-fit flex-wrap">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.key ? "bg-[#C9A84C] text-black" : "text-zinc-500 hover:text-white"
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "cursisten"    && (
          <CursistenTab
            users={users} stats={stats} loading={loading}
            api={api} token={adminUser?.token}
            unreadCounts={unreadCounts}
            onUserDeleted={handleUserDeleted}
            onUserUpdated={handleUserUpdated}
            onSchemaPanel={setCursistSchemaPanel}
            onBericht={setTrainerBerichtModal}
            onShowCreate={() => setShowCreate(true)}
            showToast={showToast}
            onGoToTrainModus={() => setActiveTab("trainmodus")}
          />
        )}
        {activeTab === "bestellingen" && <BestellingenTab token={adminUser?.token} onGoToMaker={() => setActiveTab("maker")} />}
        {activeTab === "ptverkoop"    && <PTVerkoopTab    token={adminUser?.token} />}
        {activeTab === "facturen"     && <FacturenTab     token={adminUser?.token} />}
        {activeTab === "maker"        && (
          <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl overflow-hidden" style={{ minHeight: "70vh" }}>
            <SchemaBuilder adminToken={adminUser?.token} />
          </div>
        )}
        {activeTab === "schemas"      && !loading && (
          <SchemasView users={users} api={api} onRefresh={load} showToast={showToast} token={adminUser?.token || ""} />
        )}
        {activeTab === "bewerken"     && <SchemaEditorView   api={api} showToast={showToast} token={adminUser?.token} />}
        {activeTab === "voortgang"    && <VoortgangTab       token={adminUser?.token} />}
        {activeTab === "oefeningen"   && <OefenbibliotheekTab token={adminUser?.token} />}
        {activeTab === "analyse"      && <AnalyticsTab       token={adminUser?.token} />}
        {activeTab === "bedrijven"    && <BedrijvenTab       token={adminUser?.token} />}
        {activeTab === "abonnementen" && <AbonnementenTab    token={adminUser?.token} />}
        {activeTab === "templates"    && <SchemaTemplatesTab token={adminUser?.token} />}
        {activeTab === "kortingscodes"&& <KortingcodesTab    token={adminUser?.token} />}
        {activeTab === "trainmodus"   && <TrainModusTab      token={adminUser?.token} />}
        {activeTab === "voeding"      && <VoedingTab         token={adminUser?.token} />}
        {activeTab === "rooster"      && <AdminRoosterTab    token={adminUser?.token} />}
        {activeTab === "credits"      && <AdminCreditsTab    token={adminUser?.token} />}
        {activeTab === "community"    && (
          <AdminCommunityTab
            token={adminUser?.token}
            currentUserId={adminUser?.id || adminUser?._id}
            currentRole="admin"
            showBedrijf={true}
          />
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { load(); showToast("Cursist aangemaakt!"); }}
          api={api}
        />
      )}
      {trainerBerichtModal && (
        <ConversatieModal
          user={trainerBerichtModal}
          token={adminUser?.token}
          onClose={() => setTrainerBerichtModal(null)}
          onUnreadCleared={handleUnreadCleared}
        />
      )}
      {cursistSchemaPanel && (
        <CursistSchemaPanel
          cursist={cursistSchemaPanel}
          token={adminUser?.token}
          onClose={() => setCursistSchemaPanel(null)}
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
